import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ChangeDetectorRef,
  ViewChild,
  ElementRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TavusCviService, TavusConversation } from 'src/app/core/services/tavus-cvi.service';

@Component({
  selector: 'app-ai-avatar-widget',
  templateUrl: './ai-avatar-widget.component.html',
  styleUrls: ['./ai-avatar-widget.component.scss']
})
export class AiAvatarWidgetComponent implements OnInit, OnDestroy {
  public isOpen: boolean = false;
  public isMinimized: boolean = false;
  public isLoading: boolean = false;
  public isVideoReady: boolean = false;
  public errorMessage: string | null = null;
  public conversation: TavusConversation | null = null;

  // Permission states
  public permissionDenied: boolean = false;
  public permissionBlocked: boolean = false;

  // Audio / Mute state
  public isMuted: boolean = false;

  // Background session limits
  private readonly SESSION_LIMIT_MS: number = 7 * 60 * 1000; // 7 minutes active connection limit
  private readonly INACTIVITY_LIMIT_MS: number = 45 * 1000;  // 45 seconds without sound/talking/action
  private readonly MINIMIZE_LIMIT_MS: number = 45 * 1000;   // 45 seconds minimized limit

  // Audio activity detection (mic sound and avatar voice)
  private audioContext: AudioContext | null = null;
  private micAnalyser: AnalyserNode | null = null;
  private avatarAnalyser: AnalyserNode | null = null;
  private micSourceNode: MediaStreamAudioSourceNode | null = null;
  private avatarSourceNode: MediaStreamAudioSourceNode | null = null;
  private currentLocalTrack: MediaStreamTrack | null = null;
  private currentAvatarTrack: MediaStreamTrack | null = null;

  private sessionTimeout: any = null;
  private minimizeTimeout: any = null;
  private inactivityInterval: any = null;
  private audioMonitoringInterval: any = null;
  private lastActivityTimestamp: number = Date.now();

  // Headless WebRTC Call Object (no meeting iframe)
  private callObject: any = null;

  @ViewChild('avatarVideo') avatarVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('avatarAudio') avatarAudioRef!: ElementRef<HTMLAudioElement>;

  private subs: Subscription = new Subscription();

  constructor(
    private tavusService: TavusCviService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Preload Daily.js library
    this.ensureDailyLoaded().catch(err => {
      console.warn('Daily.js preload notice:', err);
    });

    // Subscribe to service errors
    this.subs.add(
      this.tavusService.error$.subscribe(err => {
        if (err) {
          this.errorMessage = err;
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.clearAllLimits();
    this.subs.unsubscribe();
    this.destroyCallObject();
    if (this.conversation?.conversation_id) {
      this.tavusService.endConversation(this.conversation.conversation_id);
    }
  }

  /**
   * Dynamically ensure Daily.js library is loaded
   */
  private async ensureDailyLoaded(): Promise<any> {
    if ((window as any).Daily) {
      return (window as any).Daily;
    }

    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src*="daily-js"]');
      if (existing) {
        existing.addEventListener('load', () => resolve((window as any).Daily));
        existing.addEventListener('error', reject);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@daily-co/daily-js';
      script.async = true;
      script.onload = () => resolve((window as any).Daily);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Toggle the popup widget when floating button is clicked
   */
  public toggleWidget(): void {
    if (this.isOpen && this.isMinimized) {
      this.restoreWidget();
    } else if (!this.isOpen) {
      this.openWidget();
    } else {
      this.minimizeWidget();
    }
  }

  /**
   * Floating launcher click handler
   */
  public onLauncherClick(event: MouseEvent): void {
    event.stopPropagation();
    this.initAudioContext();
    if (this.isOpen && this.isMinimized) {
      this.restoreWidget();
    } else if (!this.isOpen) {
      this.openWidget();
    } else {
      this.minimizeWidget();
    }
  }

  /**
   * Open the popup widget and initiate conversation
   */
  public async openWidget(): Promise<void> {
    this.initAudioContext();
    this.isOpen = true;
    this.isMinimized = false;
    this.errorMessage = null;

    const hasPermission = await this.checkMicrophonePermission();
    if (hasPermission) {
      await this.startSession();
    }
  }

  /**
   * Minimize the widget back into the launcher icon
   */
  public minimizeWidget(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.isMinimized = true;
    this.startMinimizeTimer();
    this.cdr.markForCheck();
  }

  /**
   * Restore the widget from minimized state
   */
  public restoreWidget(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.isMinimized = false;
    this.clearMinimizeTimer();
    this.lastActivityTimestamp = Date.now();
    this.cdr.markForCheck();
  }

  /**
   * Close the popup widget and terminate the session completely
   */
  public async closeWidget(event?: MouseEvent): Promise<void> {
    if (event) {
      event.stopPropagation();
    }
    this.clearAllLimits();
    this.isOpen = false;
    this.isMinimized = false;
    this.destroyCallObject();
    await this.endSession();
    this.cdr.markForCheck();
  }

  /**
   * Toggle minimized state (backward compatibility)
   */
  public toggleMinimize(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.isMinimized) {
      this.restoreWidget(event);
    } else {
      this.minimizeWidget(event);
    }
  }

  /**
   * Check & request microphone permission
   */
  public async checkMicrophonePermission(): Promise<boolean> {
    this.permissionBlocked = false;
    this.permissionDenied = false;

    // Check query status if available
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const queryStatus = await navigator.permissions.query({ name: 'microphone' as any });
        if (queryStatus.state === 'denied') {
          this.permissionBlocked = true;
          this.cdr.markForCheck();
          return false;
        }
      } catch (_) {
        // Not all browsers support query for microphone
      }
    }

    // Attempt to acquire microphone stream
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      // Stop probe tracks immediately
      stream.getTracks().forEach(track => track.stop());
      this.permissionBlocked = false;
      this.permissionDenied = false;
      this.cdr.markForCheck();
      return true;
    } catch (err: any) {
      console.warn('Microphone permission check error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        // Re-check if permanently denied
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const queryStatus = await navigator.permissions.query({ name: 'microphone' as any });
            if (queryStatus.state === 'denied') {
              this.permissionBlocked = true;
              this.cdr.markForCheck();
              return false;
            }
          } catch (_) {}
        }
        this.permissionDenied = true;
      } else {
        this.errorMessage = 'Microphone not detected or unavailable.';
      }
      this.cdr.markForCheck();
      return false;
    }
  }

  /**
   * Retry requesting permission and start session
   */
  public async requestPermissionAndStart(): Promise<void> {
    const granted = await this.checkMicrophonePermission();
    if (granted) {
      await this.startSession();
    }
  }

  /**
   * Start a new Tavus conversation session and connect headless WebRTC
   */
  public async startSession(): Promise<void> {
    this.errorMessage = null;
    this.isLoading = true;
    this.isVideoReady = false;
    this.clearAllLimits();
    this.cdr.markForCheck();

    try {
      const conversation = await this.tavusService.startConversation({
        conversationName: 'Mihuru Signup AI Assistant',
        context: 'You are Mihuru AI, a friendly and professional travel financing advisor. Help the customer understand Mihuru travel credit lines, flexible monthly EMI plans, zero impact on credit scores during initial checks, and guide them through signing up smoothly.'
      });

      this.conversation = conversation;

      // Mount Headless WebRTC Call Object
      setTimeout(() => {
        this.startCallObject(conversation.conversation_url);
      }, 50);

    } catch (err: any) {
      console.error('Failed to start Tavus session:', err);
      this.errorMessage = err?.message || 'Unable to start AI conversation. Please try again.';
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Force Daily SFU to stream the highest quality video layer (layer 2: 720p/1080p)
   */
  private async applyHighQualityReceiveSettings(): Promise<void> {
    if (!this.callObject) return;
    try {
      // Daily format: only 'base' and valid participant session IDs are allowed
      const settings: any = {
        base: {
          video: { layer: 2 }
        }
      };

      const participants = typeof this.callObject.participants === 'function' ? this.callObject.participants() : null;
      if (participants) {
        for (const id in participants) {
          if (!participants[id].local) {
            settings[id] = {
              video: { layer: 2 }
            };
          }
        }
      }

      await this.callObject.updateReceiveSettings(settings);

      // Remove any client-side bandwidth limits
      try {
        this.callObject.setBandwidth({
          kbs: 'NO_CAP',
          trackConstraints: {
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 },
            frameRate: { ideal: 30 }
          }
        });
      } catch (_) {}
    } catch (err) {
      console.warn('Daily updateReceiveSettings notice (non-fatal):', err);
    }
  }

  /**
   * Start Headless WebRTC CallObject (Direct media stream into <video> and <audio>)
   */
  private async startCallObject(conversationUrl: string): Promise<void> {
    try {
      await this.ensureDailyLoaded();
      const Daily = (window as any).Daily;

      if (!Daily) {
        throw new Error('Daily.js library failed to load.');
      }

      this.destroyCallObject();

      // Create Headless CallObject with automatic track subscription
      this.callObject = Daily.createCallObject({
        subscribeToTracksAutomatically: true,
      });

      // Handle incoming WebRTC audio/video tracks from the Tavus AI Avatar
      this.callObject.on('track-started', (event: any) => {
        if (event.participant && !event.participant.local) {
          this.attachRemoteTrack(event.track);
          this.applyHighQualityReceiveSettings();
        }
      });

      this.callObject.on('track-stopped', (event: any) => {
        // If avatar video track stops
      });

      this.callObject.on('joined-meeting', () => {
        this.applyHighQualityReceiveSettings();
        this.checkExistingTracks();
        this.checkLocalMicTrack();
      });

      this.callObject.on('participant-joined', (event: any) => {
        if (!event.participant?.local) {
          this.applyHighQualityReceiveSettings();
          this.checkExistingTracks();
        } else {
          this.checkLocalMicTrack();
        }
      });

      this.callObject.on('participant-updated', (event: any) => {
        if (!event.participant?.local) {
          this.applyHighQualityReceiveSettings();
          this.checkExistingTracks();
        } else {
          this.checkLocalMicTrack();
        }
      });

      this.callObject.on('active-speaker-change', () => {
        this.lastActivityTimestamp = Date.now();
      });

      this.callObject.on('left-meeting', () => {
        this.closeWidget();
      });

      this.callObject.on('error', (err: any) => {
        console.error('Daily WebRTC error:', err);
      });

      this.callObject.on('camera-error', (err: any) => {
        console.warn('Daily microphone/device error:', err);
        if (err?.errorMsg?.includes('permission') || err?.error?.type === 'permissions') {
          this.permissionDenied = true;
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });

      // Join the conversation headless: NO camera for user, mic active, layer 2 base
      await this.callObject.join({
        url: conversationUrl,
        userName: 'Traveler',
        startVideoOff: true,  // User camera is completely OFF
        startAudioOff: false, // User mic is ON so user can speak with avatar
        receiveSettings: {
          base: {
            video: { layer: 2 }
          }
        }
      });

      // Re-apply receive settings after join to enforce layer 2
      await this.applyHighQualityReceiveSettings();

    } catch (err: any) {
      console.error('Failed to initialize WebRTC call object:', err);
      this.errorMessage = err?.message || 'Failed to initialize video assistant stream.';
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Attach remote video and audio tracks to native HTML elements
   */
  private attachRemoteTrack(track: MediaStreamTrack): void {
    if (track.kind === 'video' && this.avatarVideoRef?.nativeElement) {
      const videoEl = this.avatarVideoRef.nativeElement;
      videoEl.srcObject = new MediaStream([track]);
      videoEl.play().catch(e => console.warn('Video play warning:', e));
      this.isVideoReady = true;
      this.isLoading = false;
      this.applyHighQualityReceiveSettings();
      this.startSessionLimits();
      this.cdr.markForCheck();
    }

    if (track.kind === 'audio' && this.avatarAudioRef?.nativeElement) {
      const audioEl = this.avatarAudioRef.nativeElement;
      audioEl.srcObject = new MediaStream([track]);
      audioEl.play().catch(e => console.warn('Audio play warning:', e));
      if (track !== this.currentAvatarTrack) {
        this.currentAvatarTrack = track;
        this.setupTrackAnalyser(track, 'avatar');
      }
    }
  }

  /**
   * Check if avatar tracks already exist on any remote participant
   */
  private checkExistingTracks(): void {
    if (!this.callObject) return;
    const participants = this.callObject.participants();
    for (const id in participants) {
      const p = participants[id];
      if (!p.local && p.tracks) {
        if (p.tracks.video?.persistentTrack) {
          this.attachRemoteTrack(p.tracks.video.persistentTrack);
        }
        if (p.tracks.audio?.persistentTrack) {
          this.attachRemoteTrack(p.tracks.audio.persistentTrack);
        }
      }
    }
  }

  /**
   * Toggle microphone mute / unmute
   */
  public toggleMute(): void {
    if (!this.callObject) return;
    this.isMuted = !this.isMuted;
    this.callObject.setLocalAudio(!this.isMuted);
    this.cdr.markForCheck();
  }

  /**
   * Clean up Daily CallObject instance
   */
  private destroyCallObject(): void {
    if (this.callObject) {
      try {
        this.callObject.leave();
        this.callObject.destroy();
      } catch (err) {
        console.warn('Error destroying call object:', err);
      }
      this.callObject = null;
    }
    if (this.avatarVideoRef?.nativeElement) {
      this.avatarVideoRef.nativeElement.srcObject = null;
    }
    if (this.avatarAudioRef?.nativeElement) {
      this.avatarAudioRef.nativeElement.srcObject = null;
    }
    this.isVideoReady = false;
    this.isMuted = false;
  }

  /**
   * End the current Tavus conversation session
   */
  public async endSession(): Promise<void> {
    if (this.conversation?.conversation_id) {
      await this.tavusService.endConversation(this.conversation.conversation_id);
    }
    this.conversation = null;
    this.errorMessage = null;
    this.isLoading = false;
    this.isVideoReady = false;
    this.permissionBlocked = false;
    this.permissionDenied = false;
    this.cdr.markForCheck();
  }

  /**
   * Retry starting the session
   */
  public retry(): void {
    this.startSession();
  }

  /**
   * Ensure session cleanup on page unload / tab close
   */
  @HostListener('window:beforeunload')
  public onBeforeUnload(): void {
    if (this.callObject) {
      try { this.callObject.leave(); } catch (_) {}
    }
    if (this.conversation?.conversation_id) {
      this.tavusService.endConversation(this.conversation.conversation_id);
    }
  }

  @HostListener('window:pagehide')
  public onPageHide(): void {
    if (this.callObject) {
      try { this.callObject.leave(); } catch (_) {}
    }
    if (this.conversation?.conversation_id) {
      this.tavusService.endConversation(this.conversation.conversation_id);
    }
  }

  /**
   * Start 7-minute session limit and 45s inactivity tracking
   */
  private startSessionLimits(): void {
    this.clearAllLimits();
    this.lastActivityTimestamp = Date.now();
    this.startAudioMonitoring();
    this.checkLocalMicTrack();

    // 1. Session duration limit: 7 minutes -> disconnect
    this.sessionTimeout = setTimeout(() => {
      if (this.isOpen) {
        this.closeWidget();
      }
    }, this.SESSION_LIMIT_MS);

    // 2. Inactivity tracking: 45s without sound in mic, avatar talking, or user action -> disconnect
    this.inactivityInterval = setInterval(() => {
      if (this.isOpen && !this.isMinimized && this.isVideoReady) {
        const elapsed = Date.now() - this.lastActivityTimestamp;
        if (elapsed >= this.INACTIVITY_LIMIT_MS) {
          this.closeWidget();
        }
      }
    }, 1000);

    if (this.isMinimized) {
      this.startMinimizeTimer();
    }
  }

  /**
   * Start 45s timer when minimized during active session -> disconnect
   */
  private startMinimizeTimer(): void {
    this.clearMinimizeTimer();
    if (this.isOpen && (this.isVideoReady || this.isLoading)) {
      this.minimizeTimeout = setTimeout(() => {
        if (this.isMinimized && this.isOpen) {
          this.closeWidget();
        }
      }, this.MINIMIZE_LIMIT_MS);
    }
  }

  /**
   * Clear minimize timer
   */
  private clearMinimizeTimer(): void {
    if (this.minimizeTimeout) {
      clearTimeout(this.minimizeTimeout);
      this.minimizeTimeout = null;
    }
  }

  /**
   * Clear all background timers and stop audio monitoring
   */
  private clearAllLimits(): void {
    if (this.sessionTimeout) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = null;
    }
    if (this.inactivityInterval) {
      clearInterval(this.inactivityInterval);
      this.inactivityInterval = null;
    }
    this.clearMinimizeTimer();
    this.stopAudioMonitoring();
  }

  /**
   * Initialize AudioContext on user gesture (safe for iOS Safari & Android Chrome)
   */
  private initAudioContext(): void {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtx) {
        if (!this.audioContext || this.audioContext.state === 'closed') {
          this.audioContext = new AudioCtx();
        }
        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume().catch(() => {});
        }
      }
    } catch (e) {
      console.warn('AudioContext init notice:', e);
    }
  }

  /**
   * Connect an audio track to an AnalyserNode to detect speech / sound
   */
  private setupTrackAnalyser(track: MediaStreamTrack, type: 'mic' | 'avatar'): void {
    try {
      this.initAudioContext();
      if (!this.audioContext || this.audioContext.state === 'closed') {
        return;
      }

      const stream = new MediaStream([track]);
      const sourceNode = this.audioContext.createMediaStreamSource(stream);
      const analyserNode = this.audioContext.createAnalyser();
      analyserNode.fftSize = 256;
      analyserNode.smoothingTimeConstant = 0.3;
      sourceNode.connect(analyserNode);

      if (type === 'mic') {
        if (this.micSourceNode) {
          try { this.micSourceNode.disconnect(); } catch (_) {}
        }
        this.micSourceNode = sourceNode;
        this.micAnalyser = analyserNode;
      } else {
        if (this.avatarSourceNode) {
          try { this.avatarSourceNode.disconnect(); } catch (_) {}
        }
        this.avatarSourceNode = sourceNode;
        this.avatarAnalyser = analyserNode;
      }
    } catch (err) {
      console.warn(`Could not setup ${type} audio analyser:`, err);
    }
  }

  /**
   * Check for local microphone track from Daily call object
   */
  private checkLocalMicTrack(): void {
    if (!this.callObject) return;
    try {
      const localParticipant = this.callObject.participants()?.local;
      const audioTrack = localParticipant?.tracks?.audio?.persistentTrack || localParticipant?.tracks?.audio?.track;
      if (audioTrack && audioTrack !== this.currentLocalTrack && audioTrack.readyState === 'live') {
        this.currentLocalTrack = audioTrack;
        this.setupTrackAnalyser(audioTrack, 'mic');
      }
    } catch (_) {}
  }

  /**
   * Monitor audio volume levels every 200ms from user mic and avatar voice
   */
  private startAudioMonitoring(): void {
    this.stopAudioMonitoring();
    const buffer = new Uint8Array(128);

    this.audioMonitoringInterval = setInterval(() => {
      if (!this.isOpen || this.isMinimized || !this.isVideoReady) {
        return;
      }

      let isSoundDetected = false;

      // 1. Check local microphone for user speech
      if (this.micAnalyser && !this.isMuted) {
        this.micAnalyser.getByteFrequencyData(buffer);
        let sum = 0;
        for (let i = 0; i < buffer.length; i++) {
          sum += buffer[i];
        }
        const avg = sum / buffer.length;
        if (avg > 6) {
          isSoundDetected = true;
        }
      }

      // 2. Check avatar audio for AI talking
      if (this.avatarAnalyser && !isSoundDetected) {
        this.avatarAnalyser.getByteFrequencyData(buffer);
        let sum = 0;
        for (let i = 0; i < buffer.length; i++) {
          sum += buffer[i];
        }
        const avg = sum / buffer.length;
        if (avg > 6) {
          isSoundDetected = true;
        }
      }

      // 3. Fallback: check Daily participant audio level if available
      if (!isSoundDetected && this.callObject) {
        try {
          const participants = this.callObject.participants();
          for (const id in participants) {
            const p = participants[id];
            if (p.audioLevel && p.audioLevel > 0.04) {
              isSoundDetected = true;
              break;
            }
          }
        } catch (_) {}
      }

      if (isSoundDetected) {
        this.lastActivityTimestamp = Date.now();
      }
    }, 200);
  }

  /**
   * Stop audio monitoring and release audio nodes
   */
  private stopAudioMonitoring(): void {
    if (this.audioMonitoringInterval) {
      clearInterval(this.audioMonitoringInterval);
      this.audioMonitoringInterval = null;
    }
    if (this.micSourceNode) {
      try { this.micSourceNode.disconnect(); } catch (_) {}
      this.micSourceNode = null;
    }
    if (this.avatarSourceNode) {
      try { this.avatarSourceNode.disconnect(); } catch (_) {}
      this.avatarSourceNode = null;
    }
    this.micAnalyser = null;
    this.avatarAnalyser = null;
    this.currentLocalTrack = null;
    this.currentAvatarTrack = null;
    if (this.audioContext && this.audioContext.state !== 'closed') {
      try { this.audioContext.close(); } catch (_) {}
      this.audioContext = null;
    }
  }

  /**
   * Detect user interaction (mouse, touch, keyboard, scroll) on desktop and mobile
   */
  @HostListener('window:mousemove')
  @HostListener('window:keydown')
  @HostListener('window:touchstart')
  @HostListener('window:touchmove')
  @HostListener('window:click')
  @HostListener('window:scroll')
  public onUserActivity(): void {
    if (this.isOpen && !this.isMinimized && this.isVideoReady) {
      this.lastActivityTimestamp = Date.now();
    }
  }
}
